import React, { useState, useEffect, useRef } from "react";
import { X, Copy, Check, Loader2, CheckCircle2, CreditCard, QrCode, ShoppingCart, Calendar, Shield, Gem } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MP_PUBLIC_KEY = process.env.REACT_APP_MP_PUBLIC_KEY;
const CHAVE_PIX = "suporte@monyxx.com.br";

const PLANOS = [
  {
    id: "mensal",
    label: "Mensal",
    precoLabel: "R$ 14,90/mês",
    amount: 14.90,
    pix_key: "mensal_mensal",
    hotmart: process.env.REACT_APP_HOTMART_MENSAL || "https://pay.hotmart.com/U106046685M",
    Icon: Calendar,
    cor: "text-blue-400",
  },
  {
    id: "anual-essencial",
    label: "Anual Essencial",
    precoLabel: "R$ 99,90/ano",
    amount: 99.90,
    pix_key: "essencial_anual",
    hotmart: process.env.REACT_APP_HOTMART_ANUAL_ESSENCIAL || "https://pay.hotmart.com/C106046581R?off=x84kn24y",
    Icon: Shield,
    cor: "text-emerald-400",
  },
  {
    id: "anual-premium",
    label: "Anual Premium",
    precoLabel: "R$ 139,90/ano",
    amount: 139.90,
    pix_key: "premium_anual",
    hotmart: process.env.REACT_APP_HOTMART_ANUAL_PREMIUM || "https://pay.hotmart.com/K106046660U?off=47s9loc0",
    Icon: Gem,
    cor: "text-amber-400",
  },
];

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

function PlanoSelector({ selected, onChange }) {
  return (
    <div className="mb-5">
      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Plano</p>
      <div className="space-y-2">
        {PLANOS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${
              selected === p.id
                ? "border-emerald-500 bg-emerald-500/10"
                : "border-slate-700/50 bg-slate-800/40 hover:border-slate-600"
            }`}
          >
            <p.Icon className={`w-5 h-5 shrink-0 ${selected === p.id ? "text-emerald-400" : p.cor}`} />
            <span className={`font-semibold flex-1 ${selected === p.id ? "text-white" : "text-slate-200"}`}>
              {p.label}
            </span>
            <span className="text-sm font-bold text-emerald-400">{p.precoLabel}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LandingPaymentModal({ isOpen, onClose, plano: planoProp }) {
  const [tab, setTab]         = useState("mp");
  const [planoId, setPlanoId] = useState(planoProp || "anual-premium");
  const [success, setSuccess] = useState(null); // null | "pagamento" | "pix-avulso"
  const [email, setEmail]     = useState("");

  useEffect(() => {
    if (isOpen) {
      setTab("mp");
      setPlanoId(planoProp || "anual-premium");
      setSuccess(null);
      setEmail("");
      // Ping para acordar o backend (Render free tier dorme após inatividade)
      if (API) fetch(`${API}/api/health`).catch(() => {});
    }
  }, [isOpen, planoProp]);

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <div className="relative w-full max-w-md bg-[#0D1B2E] border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <SuccessStep type={success} email={email} onClose={onClose} />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-md bg-[#0D1B2E] border border-slate-700/50 rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
          <h2 className="text-white font-bold text-xl">Escolha seu Plano</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-3 pb-0 border-b border-slate-700/50">
          {[
            { key: "mp",         label: "Mercado Pago" },
            { key: "hotmart",    label: "Hotmart" },
            { key: "pix-avulso", label: "PIX Avulso" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors ${
                tab === t.key
                  ? "bg-emerald-500 text-slate-950"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {tab === "mp" && (
            <MpTab
              planoId={planoId}
              onPlanoChange={setPlanoId}
              onSuccess={(e) => { setEmail(e); setSuccess("pagamento"); }}
            />
          )}
          {tab === "hotmart" && (
            <HotmartTab planoId={planoId} onPlanoChange={setPlanoId} />
          )}
          {tab === "pix-avulso" && (
            <PixAvulsoTab
              planoId={planoId}
              onPlanoChange={setPlanoId}
              onSuccess={() => setSuccess("pix-avulso")}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Mercado Pago Tab ──────────────────────────────────────────────────────────

function MpTab({ planoId, onPlanoChange, onSuccess }) {
  const [metodo, setMetodo] = useState("pix");
  const [email, setEmail]   = useState("");
  const [step, setStep]     = useState("form"); // "form" | "pix-result" | "cartao-brick"
  const [pixData, setPixData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const plano = PLANOS.find((p) => p.id === planoId) || PLANOS[2];

  const gerar = async () => {
    if (!isValidEmail(email)) { setError("Informe um e-mail válido."); return; }
    if (metodo === "cartao") { setStep("cartao-brick"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/mercadopago/criar-pagamento`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plano: planoId, email }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Erro ao gerar PIX");
      setPixData(await res.json());
      setStep("pix-result");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  if (step === "pix-result" && pixData) {
    return <PixResult data={pixData} onSuccess={() => onSuccess(email)} />;
  }

  if (step === "cartao-brick") {
    return <CartaoBrick plano={planoId} email={email} amount={plano.amount} onSuccess={() => onSuccess(email)} />;
  }

  return (
    <div>
      <PlanoSelector selected={planoId} onChange={(id) => { onPlanoChange(id); }} />

      <div className="mb-5">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Forma de Pagamento</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMetodo("pix")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
              metodo === "pix"
                ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                : "border-slate-700 text-slate-400 hover:border-slate-500"
            }`}
          >
            <QrCode className="w-4 h-4" /> PIX
          </button>
          <button
            type="button"
            onClick={() => setMetodo("cartao")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
              metodo === "cartao"
                ? "border-blue-500 bg-blue-500/10 text-blue-400"
                : "border-slate-700 text-slate-400 hover:border-slate-500"
            }`}
          >
            <CreditCard className="w-4 h-4" /> Cartão
          </button>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">E-mail</p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-sm transition-colors"
        />
      </div>

      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

      <button
        onClick={gerar}
        disabled={loading}
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        Gerar Pagamento
      </button>
    </div>
  );
}

// ── PIX Result (Mercado Pago QR) ──────────────────────────────────────────────

function PixResult({ data, onSuccess }) {
  const [copied, setCopied]   = useState(false);
  const [seconds, setSeconds] = useState(data.expires_in || 1800);
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!data.payment_id) return;
    pollRef.current = setInterval(async () => {
      setChecking(true);
      try {
        const res = await fetch(`${API}/api/mercadopago/verificar/${data.payment_id}`);
        const j = await res.json();
        if (j.status === "approved") { clearInterval(pollRef.current); onSuccess(); }
      } catch {}
      finally { setChecking(false); }
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [data.payment_id, onSuccess]);

  const copy = () => {
    navigator.clipboard.writeText(data.qr_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="w-full flex items-center justify-between bg-slate-800/60 rounded-xl px-4 py-2">
        <span className="text-emerald-400 font-bold text-lg">
          R$ {data.amount?.toFixed(2).replace(".", ",")}
        </span>
        <span className={`text-sm font-mono font-semibold ${seconds < 300 ? "text-red-400" : "text-slate-300"}`}>
          ⏱ {fmt(seconds)}
        </span>
      </div>
      {data.qr_base64 && (
        <img
          src={`data:image/png;base64,${data.qr_base64}`}
          alt="QR Code PIX"
          className="w-48 h-48 rounded-xl border-2 border-slate-700"
        />
      )}
      {data.qr_code && (
        <div className="w-full">
          <p className="text-xs text-slate-500 mb-1">Código PIX copia e cola:</p>
          <div className="flex gap-2">
            <input
              readOnly
              value={data.qr_code}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300 font-mono truncate focus:outline-none"
            />
            <button
              onClick={copy}
              className="shrink-0 flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-3 py-2 rounded-lg text-xs transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copiado!" : "Copiar"}
            </button>
          </div>
        </div>
      )}
      <p className="text-xs text-slate-500 text-center">
        {checking ? "Verificando pagamento…" : "Verificando automaticamente a cada 10 segundos"}
      </p>
    </div>
  );
}

// ── Cartão Brick ──────────────────────────────────────────────────────────────

function CartaoBrick({ plano, email, amount, onSuccess }) {
  const brickRef = useRef(null);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!MP_PUBLIC_KEY) { setError("Chave pública do Mercado Pago não configurada."); return; }
    const existing = document.getElementById("mp-sdk");
    const init = () => {
      if (!window.MercadoPago) return;
      try {
        const mp = new window.MercadoPago(MP_PUBLIC_KEY, { locale: "pt-BR" });
        mp.bricks().create("cardPayment", "mp-card-brick", {
          initialization: { amount },
          customization: {
            paymentMethods: { maxInstallments: 6 },
            visual: { style: { theme: "dark" } },
          },
          callbacks: {
            onReady: () => {},
            onError: (e) => setError(e?.message || "Erro no formulário."),
            onSubmit: async (rawArg) => {
              const formData = rawArg?.formData ?? rawArg;
              setLoading(true); setError("");
              if (!formData?.token) {
                setError("Erro na tokenização do cartão. Verifique os dados e tente novamente.");
                setLoading(false);
                return;
              }
              try {
                const res = await fetch(`${API}/api/mercadopago/pagar-cartao`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    plano,
                    email,
                    token: formData.token,
                    payment_method_id: formData.payment_method_id,
                    installments: formData.installments ?? 1,
                    issuer_id: formData.issuer_id,
                  }),
                });
                const j = await res.json();
                if (j.status === "approved") onSuccess();
                else setError("Pagamento não aprovado. Verifique os dados do cartão.");
              } catch { setError("Erro ao conectar ao servidor. Aguarde alguns segundos e tente novamente."); }
              finally { setLoading(false); }
            },
          },
        });
        brickRef.current = true;
      } catch { setError("Erro ao inicializar formulário."); }
    };
    if (existing) { init(); return; }
    const script = document.createElement("script");
    script.id = "mp-sdk";
    script.src = "https://sdk.mercadopago.com/js/v2";
    script.onload = init;
    document.body.appendChild(script);
  }, [plano, email, amount, onSuccess]);

  return (
    <div>
      {error && <p className="text-red-400 text-sm mb-3 text-center">{error}</p>}
      {loading && (
        <div className="flex items-center justify-center py-6 gap-2">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
          <span className="text-slate-400 text-sm">Processando…</span>
        </div>
      )}
      <div id="mp-card-brick" className={loading ? "hidden" : ""} />
    </div>
  );
}

// ── Hotmart Tab ───────────────────────────────────────────────────────────────

function HotmartTab({ planoId, onPlanoChange }) {
  const plano = PLANOS.find((p) => p.id === planoId) || PLANOS[2];

  return (
    <div>
      <PlanoSelector selected={planoId} onChange={onPlanoChange} />
      <p className="text-xs text-slate-400 mb-5">
        Você será redirecionado para o checkout seguro da Hotmart. Aceita cartão, boleto e PIX.
      </p>
      <button
        onClick={() => window.open(plano.hotmart, "_blank", "noopener,noreferrer")}
        className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold py-3 rounded-xl transition-colors"
      >
        <ShoppingCart className="w-5 h-5" /> Abrir Checkout Hotmart
      </button>
    </div>
  );
}

// ── PIX Avulso Tab ────────────────────────────────────────────────────────────

function PixAvulsoTab({ planoId, onPlanoChange, onSuccess }) {
  const [nome, setNome]             = useState("");
  const [email, setEmail]           = useState("");
  const [comprovante, setComprovante] = useState("");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [copied, setCopied]         = useState(false);

  const plano = PLANOS.find((p) => p.id === planoId) || PLANOS[2];

  const copy = () => {
    navigator.clipboard.writeText(CHAVE_PIX);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!nome.trim())         { setError("Informe seu nome."); return; }
    if (!isValidEmail(email)) { setError("E-mail inválido."); return; }
    if (!comprovante.trim())  { setError("Informe o comprovante do PIX."); return; }
    setLoading(true); setError("");
    try {
      const form = new FormData();
      form.append("email", email);
      form.append("nome", nome);
      form.append("plano", plano.pix_key);
      form.append("comprovante", comprovante);
      const res = await fetch(`${API}/api/pagamento/pix/comprovante`, { method: "POST", body: form });
      if (!res.ok) throw new Error((await res.json()).detail || "Erro ao enviar.");
      onSuccess();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit}>
      {/* Chave PIX */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 mb-5">
        <p className="text-xs text-slate-400 font-semibold mb-1">Chave PIX:</p>
        <div className="flex items-center justify-between gap-2">
          <p className="text-emerald-400 font-semibold">{CHAVE_PIX}</p>
          <button type="button" onClick={copy} className="text-slate-400 hover:text-white p-1 transition-colors">
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Faça o PIX, depois preencha os campos abaixo com o comprovante.
        </p>
      </div>

      <PlanoSelector selected={planoId} onChange={onPlanoChange} />

      <div className="space-y-3 mb-4">
        <div>
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Nome</p>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Seu nome completo"
            className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-sm transition-colors"
          />
        </div>
        <div>
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">E-mail</p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-sm transition-colors"
          />
        </div>
        <div>
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">
            Comprovante / Código de Transação
          </p>
          <textarea
            value={comprovante}
            onChange={(e) => setComprovante(e.target.value)}
            placeholder="Cole aqui o ID da transação ou descreva o comprovante..."
            rows={3}
            className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-sm resize-none transition-colors"
          />
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        Enviar Comprovante
      </button>
    </form>
  );
}

// ── Success ───────────────────────────────────────────────────────────────────

function SuccessStep({ type, email, onClose }) {
  const encoded = email ? encodeURIComponent(email) : "";

  if (type === "pix-avulso") {
    return (
      <div className="flex flex-col items-center gap-5 px-6 py-10 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-400" />
        <div>
          <h3 className="text-white font-bold text-xl mb-2">Comprovante enviado!</h3>
          <p className="text-slate-400 text-sm">
            Recebemos seu comprovante e vamos confirmar o pagamento em breve.
            <br />Você receberá um e-mail assim que o plano for ativado.
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
        >
          Fechar
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-5 px-6 py-10 text-center">
      <CheckCircle2 className="w-16 h-16 text-emerald-400" />
      <div>
        <h3 className="text-white font-bold text-xl mb-2">Pagamento confirmado!</h3>
        <p className="text-slate-400 text-sm">
          Seu plano foi ativado para{" "}
          <span className="text-emerald-400 font-semibold">{email}</span>.
          <br />Crie sua conta com esse mesmo e-mail para acessar.
        </p>
      </div>
      <a
        href={`/register${encoded ? `?email=${encoded}` : ""}`}
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 rounded-xl transition-colors text-sm"
        onClick={onClose}
      >
        Criar minha conta agora
      </a>
      <a
        href={`/login${encoded ? `?email=${encoded}` : ""}`}
        className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
        onClick={onClose}
      >
        Já tenho conta — fazer login
      </a>
    </div>
  );
}
